import { requestReportShading, retitleForVintage, SHADING_UNAVAILABLE_MESSAGE } from './reportShading';
import { SHADING_2026_ENABLED } from '../featureFlags';

// Fields the DISCERN webmaps shade, and the Esri services they come from. Only
// these (block-group demographics/crime layers) can be re-shaded from 2026
// GeoEnrichment; everything else in a webmap is left exactly as authored.
export const SHADEABLE_FIELDS = new Set(['TSEGNAME', 'MEDHINC_CY', 'POPGRWCYFY', 'CRMCYTOTC']);
const SHADEABLE_SERVICE = /(USA_Demographics_and_Boundaries_\d{4}|USA_Crime(?:_\d{4})?)\/MapServer\/\d+$/i;

// Esri retired these demographics-service years (404/500 on load). Hidden
// layers that still point at them only produce console errors.
const RETIRED_SERVICE = /USA_Demographics_and_Boundaries_(20(?:0\d|1\d|21|22))(?!\d)/i;

const TOP_VALUES = 8;
const FALLBACK_COLORS = [
  [237, 125, 125, 255],
  [78, 176, 217, 255],
  [177, 207, 96, 255],
  [186, 134, 190, 255],
  [255, 170, 95, 255],
  [255, 225, 100, 255],
  [245, 160, 215, 255],
  [120, 160, 200, 255],
];

const toArray = collection => {
  if (!collection) return [];
  if (typeof collection.toArray === 'function') return collection.toArray();
  return Array.isArray(collection.items) ? collection.items : Array.isArray(collection) ? collection : [];
};

const stripTrailingSlash = url => (typeof url === 'string' ? url.replace(/\/+$/, '') : url);

export const isRetiredHiddenLayer = layer =>
  !!layer && layer.visible === false && RETIRED_SERVICE.test(layer.url || '');

/** Remove hidden layers pointing at retired services so they never try to load. */
export function removeRetiredLayers(webmap) {
  let removed = 0;
  try {
    for (const layer of toArray(webmap?.allLayers).length ? toArray(webmap.allLayers) : toArray(webmap?.layers)) {
      if (!isRetiredHiddenLayer(layer)) continue;
      const owner = layer.parent && layer.parent !== webmap && layer.parent.layers ? layer.parent : webmap;
      owner.remove(layer);
      removed += 1;
    }
  } catch (error) {
    console.warn('[discernShading] could not remove retired layers:', error?.message || error);
  }
  return removed;
}

const rendererField = renderer => renderer?.field || renderer?.field1 || null;

/**
 * Visible layers (or map-image sublayers) whose renderer shades one of the
 * DISCERN fields from a block-group Esri service. Loads layers as needed.
 */
export async function collectShadeTargets(webmap) {
  const targets = [];
  const layers = toArray(webmap?.allLayers).length ? toArray(webmap.allLayers) : toArray(webmap?.layers);

  for (const layer of layers) {
    if (!layer || layer.visible === false || layer.parent?.visible === false) continue;
    try {
      if (layer.type === 'map-image') {
        await layer.load();
        const subs = toArray(layer.allSublayers).length ? toArray(layer.allSublayers) : toArray(layer.sublayers);
        for (const sub of subs) {
          if (sub.visible === false || !sub.renderer) continue;
          const serviceURL = `${stripTrailingSlash(layer.url)}/${sub.id}`;
          const field = rendererField(sub.renderer);
          if (SHADEABLE_FIELDS.has(field) && SHADEABLE_SERVICE.test(serviceURL)) {
            targets.push({ layer, sublayer: sub, serviceURL, field, renderer: sub.renderer, title: sub.title || layer.title });
          }
        }
      } else if (layer.type === 'feature') {
        await layer.load();
        if (!layer.renderer) continue;
        const base = stripTrailingSlash(layer.url || '');
        const serviceURL = /\/\d+$/.test(base) ? base : `${base}/${layer.layerId ?? 0}`;
        const field = rendererField(layer.renderer);
        if (SHADEABLE_FIELDS.has(field) && SHADEABLE_SERVICE.test(serviceURL)) {
          targets.push({ layer, sublayer: null, serviceURL, field, renderer: layer.renderer, title: layer.title });
        }
      }
    } catch (error) {
      console.warn(`[discernShading] skipping layer ${layer?.title}:`, error?.message || error);
    }
  }
  return targets;
}

const cloneSymbol = symbol => {
  if (!symbol) return symbol;
  return typeof symbol.clone === 'function' ? symbol.clone() : JSON.parse(JSON.stringify(symbol));
};

/** Categorical (Tapestry) renderer: top values by count in the authored palette, plus "Other". */
export function buildUniqueValueRenderer(authored, features, field, legendTitle) {
  const counts = new Map();
  for (const feature of features) {
    if (feature.value === null || feature.value === undefined || feature.value === '') continue;
    counts.set(feature.value, (counts.get(feature.value) || 0) + 1);
  }
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, TOP_VALUES).map(([name]) => name);

  const authoredSymbols = toArray(authored?.uniqueValueInfos).map(info => info.symbol).filter(Boolean);
  const symbolFor = index => {
    if (authoredSymbols.length) return cloneSymbol(authoredSymbols[index % authoredSymbols.length]);
    return {
      type: 'simple-fill',
      color: FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      outline: { color: [57, 88, 92, 255], width: 0.5 },
    };
  };

  return {
    type: 'unique-value',
    field,
    uniqueValueInfos: ranked.map((name, index) => ({ value: name, label: name, symbol: symbolFor(index) })),
    defaultSymbol: { type: 'simple-fill', color: [200, 200, 200, 255], outline: { color: [57, 88, 92, 255], width: 0.5 } },
    defaultLabel: 'Other',
    legendOptions: { title: legendTitle },
  };
}

const cloneRenderer = (renderer, legendTitle) => {
  const clone = typeof renderer.clone === 'function' ? renderer.clone() : JSON.parse(JSON.stringify(renderer));
  clone.legendOptions = { ...(clone.legendOptions || {}), title: legendTitle };
  return clone;
};

/**
 * Client-side layer of the 2026 polygons that keeps the authored layer's
 * styling: the attribute is named like the authored renderer field so the
 * authored renderer (or a Tapestry one built from the 2026 values) applies as is.
 */
export function buildShadedWebmapLayer({ FeatureLayer, Graphic }, data, target) {
  const field = target.field;
  const vintage = data.vintage;
  const spatialReference = data.spatialReference?.wkid ? { wkid: data.spatialReference.wkid } : { wkid: 102100 };
  const title = retitleForVintage(target.title || '', vintage);

  const graphics = data.features.map(
    (feature, index) =>
      new Graphic({
        geometry: { type: 'polygon', rings: feature.rings, spatialReference },
        attributes: { OBJECTID: index + 1, GEOID: feature.id, [field]: feature.value },
      }),
  );

  const renderer = data.categorical
    ? buildUniqueValueRenderer(target.renderer, data.features, field, title)
    : cloneRenderer(target.renderer, title);

  const layer = new FeatureLayer({
    source: graphics,
    objectIdField: 'OBJECTID',
    geometryType: 'polygon',
    spatialReference,
    fields: [
      { name: 'OBJECTID', type: 'oid' },
      { name: 'GEOID', type: 'string' },
      { name: field, type: data.categorical ? 'string' : 'double' },
    ],
    renderer,
    title,
    popupTemplate: {
      content: `{${field}}`,
      fieldInfos: [{ fieldName: field, format: { digitSeparator: true, places: 0 } }],
    },
  });
  layer.opacity = target.sublayer?.opacity ?? target.layer.opacity ?? 0.6;
  layer.minScale = 0;
  layer.maxScale = 0;
  return layer;
}

/** Hide the authored layer and put the shaded one directly above it. */
export function replaceWithShadedLayer(webmap, target, shadedLayer) {
  const owner = target.layer.parent && target.layer.parent !== webmap && target.layer.parent.layers ? target.layer.parent : webmap;
  const collection = owner.layers;
  const index = typeof collection.indexOf === 'function' ? collection.indexOf(target.layer) : -1;

  if (target.sublayer) target.sublayer.visible = false;
  else target.layer.visible = false;

  if (index >= 0) collection.add(shadedLayer, index + 1);
  else collection.add(shadedLayer);
}

/**
 * The legend's top heading is the title of the layer's container, not the
 * layer's own: the Demographics webmap nests its block-group layer in a
 * GroupLayer titled "USA Demographics and Boundaries 2024", and map-image
 * layers carry their service title. State the year now shown on whichever of
 * those holds the title.
 */
export function relabelParentTitles(webmap, target, vintage) {
  const containers = [];
  if (target.sublayer) containers.push(target.layer);
  const group = target.layer.parent;
  if (group && group !== webmap && group.layers) containers.push(group);
  for (const container of containers) {
    if (typeof container.title === 'string') {
      container.title = retitleForVintage(container.title, vintage);
    }
  }
}

const setAuthoredVisible = (target, visible) => {
  if (target.sublayer) target.sublayer.visible = visible;
  else target.layer.visible = visible;
};

/**
 * After a Default (DISCERN) webmap has loaded, redraw its shaded layer from
 * 2026 GeoEnrichment values. Never throws.
 *
 * The authored layer is the 2024 Esri layer, so it is hidden while the 2026
 * polygons load and is only put back when the layer is not eligible for 2026
 * shading (flag off, unsupported layer). If an eligible layer FAILS to load
 * 2026 data it stays hidden and `onUnavailable(target)` is called instead of
 * showing 2024 values.
 *
 * @returns {Promise<number>} how many layers were replaced.
 */
export async function applyDiscernShading({
  webmap,
  addressInfo,
  getAccessToken,
  deps,
  fetchImpl,
  isStale = () => false,
  onUnavailable,
}) {
  let replaced = 0;
  if (!SHADING_2026_ENABLED) return 0;
  try {
    const targets = await collectShadeTargets(webmap);
    if (!targets.length || isStale()) return 0;

    // Never let the 2024 layer show while 2026 data loads.
    targets.forEach(target => setAuthoredVisible(target, false));

    await Promise.all(
      targets.map(async target => {
        try {
          const result = await requestReportShading({
            selectedBlock: { serviceURL: target.serviceURL, outfields: [target.field] },
            addressInfo,
            getAccessToken,
            ...(fetchImpl ? { fetchImpl } : {}),
          });
          if (isStale()) return;
          if (result.status === 'shaded') {
            const shadedLayer = buildShadedWebmapLayer(deps, result.data, target);
            if (isStale()) return;
            replaceWithShadedLayer(webmap, target, shadedLayer);
            relabelParentTitles(webmap, target, result.data.vintage);
            replaced += 1;
          } else if (result.status === 'not-applicable') {
            setAuthoredVisible(target, true);
          } else {
            console.warn(`[discernShading] 2026 data unavailable for ${target.title}: ${result.reason}`);
            if (typeof onUnavailable === 'function') onUnavailable(target);
          }
        } catch (error) {
          console.warn(`[discernShading] 2026 data unavailable for ${target.title}:`, error?.message || error);
          if (typeof onUnavailable === 'function') onUnavailable(target);
        }
      }),
    );
  } catch (error) {
    console.warn('[discernShading] could not apply 2026 shading:', error?.message || error);
  }
  return replaced;
}

/** Tell the user a map has no 2026 data right now (instead of drawing 2024 values). */
export function addUnavailableNotice(view, message = SHADING_UNAVAILABLE_MESSAGE) {
  try {
    if (!view?.ui || view.__vestmapShadingNotice) return;
    const el = document.createElement('div');
    el.textContent = message;
    el.style.cssText =
      'background:#fff;padding:8px 12px;border-radius:4px;font:13px sans-serif;max-width:260px;box-shadow:0 1px 4px rgba(0,0,0,.3)';
    view.ui.add(el, 'top-left');
    view.__vestmapShadingNotice = true;
  } catch (error) {
    console.warn('[discernShading] could not show notice:', error?.message || error);
  }
}
